import React from 'react';
import { useHistory, useLocation } from 'react-router';
import Text from '../../../../shared/Text';
import c42Jwt from '../../../constants/c42Jwt';
import {
  FORGOT_PASSWORD_INDEX,
  LOGIN_INDEX,
  NOT_AUTHORIZED_INCYDR_INDEX,
  RESET_PASSWORD_INDEX,
  SSO_INCORRECT_LOGIN,
  SSO_INCORRECT_LOGIN_TRY_AGAIN,
  SSO_SUCCESS_CP3,
  SUPPORT_LOGIN_INDEX,
  SUPPORT_SSO_ERROR,
} from '../../../constants/routes';
import { SupportedQueryParams } from '../../../constants/SupportedQueryParams';
import { performRedirect } from '../../../utils/performRedirect';
import { useAuthenticate, useGetServerEnv } from '../auth/hooks';
import { getRedirectParam, useSetInitialQueryParams } from '../routing/hooks';
import { DEFAULT_MAIN_DATA, MainData, MainDataContext } from './context';

const MainDataProvider: React.FC<React.PropsWithChildren<Record<never, unknown>>> = ({ children }) => {
  const [mainData, setMainData] = React.useState<MainData>(DEFAULT_MAIN_DATA);

  const history = useHistory();
  const location = useLocation();
  const setInitialQueryParams = useSetInitialQueryParams();
  const getServerEnv = useGetServerEnv();
  const authenticate = useAuthenticate();

  const getUrlSearchParam = (name: string): string => {
    const searchProps = window.location.search
      .substr(1)
      .split('&')
      .reduce((params, item) => {
        const p = item.split('=');
        params[p[0]] = p[1];
        return params;
      }, {});
    return searchProps[name];
  };

  const setInitialState = async ({ pathname, query }: { pathname: string; query: SupportedQueryParams }) => {
    const { logout, tt, to } = query;

    // Paths were we don't expect to have a valid JWT. The support login page is authenticated with
    // a cookie for STS, not a normal JWT.
    const isUnAuthedPage = [
      RESET_PASSWORD_INDEX,
      FORGOT_PASSWORD_INDEX,
      SUPPORT_LOGIN_INDEX,
      NOT_AUTHORIZED_INCYDR_INDEX,
      SSO_INCORRECT_LOGIN,
      SSO_SUCCESS_CP3,
      SSO_INCORRECT_LOGIN_TRY_AGAIN,
      SUPPORT_SSO_ERROR,
    ].includes(pathname.replace(/\/$/, ''));
    try {
      await getServerEnv();
      if (logout) {
        await c42Jwt.logout();
        history.push(LOGIN_INDEX);
      } else {
        /**
         * Check to see if user is already authenticated and redirect them to the app that requested
         * authentication if the user is authenticated. Continue to load Login app if the user is
         * not authenticated.
         */
        const loginToken = tt || getUrlSearchParam('tt');

        if (loginToken) {
          return authenticate({ loginToken })
            .then(() => performRedirect(getRedirectParam(to)))
            .catch(() =>
              setMainData((current) => ({
                ...current,
                isValidatingToken: false,
              })),
            );
        }

        if (!isUnAuthedPage) {
          c42Jwt
            .verifyAuthenticated()
            .then(() => performRedirect(getRedirectParam(to)))
            .catch(() => c42Jwt.logout());
        }
      }
    } catch {
      // authenticate failed. Go to login unless we are resetting a password, on the forgotten password page, or on the support login page
      if (isUnAuthedPage) {
        history.push(LOGIN_INDEX);
      }
    } finally {
      setMainData((current) => ({
        ...current,
        isValidatingToken: false,
      }));
    }
  };

  const initializeApplication = async () => {
    setMainData((current) => ({
      ...current,
      isValidatingToken: true,
    }));

    try {
      const { pathname, search } = location;
      const query = Object.fromEntries(new URLSearchParams(search));
      setInitialQueryParams(query);
      await setInitialState({ pathname, query });
      Text.initialize({});
      setMainData((current) => ({
        ...current,
        isInitialized: true,
        isValidatingToken: false,
      }));
    } catch (e) {
      console.error(e);
      setMainData((current) => ({
        ...current,
        isValidatingToken: false,
      }));
    }
  };

  // Initialize application
  React.useEffect(() => {
    initializeApplication();
  }, []);

  return <MainDataContext.Provider value={mainData}>{children}</MainDataContext.Provider>;
};

export default MainDataProvider;
